import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, type NutritionSummary } from "../../lib/api";
import { EmptyState } from "../../components/EmptyState";
import { fmtKcal, fmtNumber } from "../../lib/format";
import { DAY_LABEL, VerdictPill } from "./_shared";

/** Where today stands against its target, and what is left to play with. */
export default function NutritionToday() {
  const [data, setData] = useState<NutritionSummary | null | "error">(null);
  const [editing, setEditing] = useState(false);
  const [cal, setCal] = useState("");
  const [prot, setProt] = useState("");

  function load() {
    api.nutrition.summary(30).then(setData).catch(() => setData("error"));
  }
  useEffect(load, []);

  const d = data && data !== "error" ? data : null;
  const today = d?.today_row;
  if (!d || !today) {
    return data === "error" ? (
      <EmptyState error title="Couldn't load today" />
    ) : (
      <div className="text-ink-subtle">Loading…</div>
    );
  }

  const remaining = Math.round(d.targets.calories - today.calories);
  const pct = Math.min(100, Math.round((today.calories / d.targets.calories) * 100));
  const proteinPct = Math.min(100, Math.round(((today.protein_g ?? 0) / d.targets.protein_g) * 100));

  async function saveTargets() {
    await api.nutrition.setTargets({
      calories: cal ? Number(cal) : undefined,
      protein_g: prot ? Number(prot) : undefined,
    });
    setEditing(false);
    load();
  }

  return (
    <div className="animate-fade-in">
      <div className="mb-4 flex items-baseline justify-between gap-3">
        <h2 className="section-title">Today</h2>
        <VerdictPill verdict={today.verdict} />
      </div>

      <div className="card card-pad">
        <div className="flex items-baseline justify-between">
          <div>
            <div className="caption">Eaten</div>
            <div className="amount-display mt-1 text-display leading-none">{fmtKcal(today.calories)}</div>
          </div>
          <div className="text-right">
            <div className="caption">{remaining >= 0 ? "Left" : "Over"}</div>
            <div className={`font-display text-2xl tabular-nums ${remaining >= 0 ? "text-ink" : "text-loss"}`}>
              {fmtKcal(Math.abs(remaining))}
            </div>
          </div>
        </div>

        <div className="mt-3 h-2 w-full overflow-hidden rounded-full bg-ink-line">
          <div
            className={`h-2 rounded-full transition-all duration-slow ${remaining >= 0 ? "bg-accent" : "bg-loss"}`}
            style={{ width: `${pct}%` }}
          />
        </div>
        <div className="mt-1.5 text-xs text-ink-subtle">
          Target {fmtKcal(d.targets.calories)} ·{" "}
          {d.targets.source === "rp"
            ? `${d.targets.phase} phase, ${DAY_LABEL[d.targets.day_type]} day`
            : d.targets.source === "goal"
              ? "your goal"
              : "worked out from your weight, steps and workouts"}
        </div>

        <div className="mt-4 grid grid-cols-4 gap-2">
          <Tile label="Protein" value={`${fmtNumber(today.protein_g ?? 0, 0)}g`} sub={`of ${d.targets.protein_g}g`} strong={proteinPct >= 80} />
          <Tile label="Carbs" value={`${fmtNumber(today.carbs_g ?? 0, 0)}g`} sub={today.carbs_target != null ? `of ${today.carbs_target}g` : undefined} />
          <Tile label="Fat" value={`${fmtNumber(today.fat_g ?? 0, 0)}g`} sub={today.fat_target != null ? `of ${today.fat_target}g` : undefined} />
          <Tile label="Fibre" value={`${fmtNumber(today.fiber_g ?? 0, 0)}g`} />
        </div>
      </div>

      {d.targets.source === "rp" && (
        <div className="card card-pad mt-4">
          <div className="flex items-baseline justify-between">
            <div className="caption">Training day</div>
            <span className="text-xs text-ink-subtle">
              {today.day_type_set ? "set by you" : "guessed from your training"}
            </span>
          </div>
          <div className="mt-2 flex gap-1.5">
            {(["rest", "light", "moderate", "hard"] as const).map((t) => (
              <button
                key={t}
                type="button"
                onClick={async () => {
                  await api.diet.setDayType(t);
                  load();
                }}
                className={`flex-1 rounded-md border px-2 py-2 text-xs capitalize transition duration-fast ${
                  today.day_type === t
                    ? "border-accent bg-accent text-white"
                    : "border-ink-line bg-surface text-ink-muted hover:border-ink-subtle/60"
                }`}
              >
                {t}
              </button>
            ))}
          </div>
          <p className="mt-2 text-xs text-ink-subtle">
            Carbs move with the day: {today.carbs_target}g today, against {d.targets.protein_g}g protein and{" "}
            {d.targets.fat_g}g fat, which stay put. {d.targets.veggie_cups} cups of veg as well.
          </p>
        </div>
      )}

      <div className="card card-pad mt-4">
        <div className="caption">Energy today</div>
        <div className="mt-2 grid grid-cols-3 gap-2">
          <Tile label="Burned" value={fmtKcal(today.maintenance)} sub="resting + steps + workouts" />
          <Tile label="Eaten" value={fmtKcal(today.calories)} />
          <Tile
            label="Balance"
            value={`${today.calories > 0 && today.calories - today.maintenance > 0 ? "+" : ""}${fmtKcal(today.calories > 0 ? today.calories - today.maintenance : 0)}`}
            sub={today.calories > 0 && today.calories < today.maintenance ? "in deficit" : undefined}
          />
        </div>
        <p className="mt-2 text-xs text-ink-subtle">
          {today.steps != null
            ? `${fmtNumber(today.steps, 0)} steps so far, which is most of the difference between a low day and a high one.`
            : "No steps recorded yet today."}
        </p>
      </div>

      <div className="card card-pad mt-4">
        <div className="flex items-baseline justify-between">
          <div className="caption">Targets</div>
          <button type="button" className="text-xs text-accent" onClick={() => setEditing((v) => !v)}>
            {editing ? "Cancel" : "Set your own"}
          </button>
        </div>
        {editing ? (
          <div className="mt-3 space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <label className="block">
                <span className="field-label">Calories</span>
                <input className="input mt-1" inputMode="numeric" value={cal} placeholder={String(d.targets.calories)} onChange={(e) => setCal(e.target.value)} />
              </label>
              <label className="block">
                <span className="field-label">Protein g</span>
                <input className="input mt-1" inputMode="numeric" value={prot} placeholder={String(d.targets.protein_g)} onChange={(e) => setProt(e.target.value)} />
              </label>
            </div>
            <button type="button" className="btn-accent w-full py-2" onClick={saveTargets}>
              Save targets
            </button>
            <p className="text-xs text-ink-subtle">Leave a field empty to go back to the worked-out figure.</p>
          </div>
        ) : (
          <p className="mt-2 text-sm text-ink-muted">
            {fmtKcal(d.targets.calories)} and {d.targets.protein_g}g protein a day.
            {d.targets.source === "rp"
              ? ` From your RP plan — the ${d.targets.phase} phase on a ${DAY_LABEL[d.targets.day_type]} day. Setting your own here overrides it.`
              : d.targets.source === "estimated" && d.targets.goal_weight_kg
                ? ` That is about 550 kcal under what today burns, which is half a kilo a week towards ${d.targets.goal_weight_kg} kg.`
                : ""}
          </p>
        )}
      </div>

      <Link to="/food" className="btn-secondary mt-4 inline-block">
        Log something
      </Link>
    </div>
  );
}

function Tile({ label, value, sub, strong }: { label: string; value: string; sub?: string; strong?: boolean }) {
  return (
    <div className="rounded-md border border-ink-line bg-canvas px-2 py-2 text-center">
      <div className="text-[10px] uppercase tracking-wider text-ink-subtle">{label}</div>
      <div className={`mt-0.5 font-display tabular-nums ${strong ? "text-accent" : "text-ink"}`}>{value}</div>
      {sub && <div className="mt-0.5 text-[10px] text-ink-subtle">{sub}</div>}
    </div>
  );
}
